import { useCallback, useRef, useState } from "react";
import type { ToastItem, ToastKind } from "@/components/ui/Toast";
import { alertsEnabled, playChime, showDesktopNotification } from "@/lib/notify";

export interface UseToastResult {
  toasts: ToastItem[];
  push: (kind: ToastKind, message: string) => void;
  dismiss: (id: number) => void;
}

// Window-popup title per toast kind. error uses the falling "exit" chime.
const TOAST_TITLE: Record<ToastKind, string> = {
  success: "✅ NSS",
  error: "⚠️ NSS",
};

export function useToast(): UseToastResult {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const counter = useRef(0);

  const push = useCallback((kind: ToastKind, message: string) => {
    counter.current += 1;
    const id = counter.current;
    setToasts((prev) => [...prev, { id, kind, message }]);
    // Mirror every toast to a window popup + chime, gated by the bell toggle
    // (showDesktopNotification also self-gates on OS permission).
    if (alertsEnabled()) {
      showDesktopNotification(TOAST_TITLE[kind], message, `nss-toast-${String(id)}`);
      playChime(kind === "error" ? "exit" : "entry");
    }
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return { toasts, push, dismiss };
}
